import { useEffect, useRef } from 'react';

const CHAMPAGNE = '#E8B96A';
const CHAMPAGNE_LIGHT = '#F0C674';
const FG = '#F5F1FA';
const MUTED = '#A99FB8';

interface ModuleSectionProps {
  id: string;
  kicker: string;
  title: string;
  desc: string | string[];
  children: React.ReactNode;
  reverse?: boolean;
}

export function ModuleSection({
  id,
  kicker,
  title,
  desc,
  children,
  reverse = false,
}: ModuleSectionProps) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('ms-visible');
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{MODULE_SECTION_STYLES}</style>
      <section
        ref={ref}
        id={id}
        className={`module-section${reverse ? ' ms-reverse' : ''}`}
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 1180,
          margin: '0 auto',
          padding: 'clamp(64px, 8vw, 112px) 24px',
          display: 'grid',
          gap: 'clamp(40px, 6vw, 72px)',
          alignItems: 'center',
        }}
      >
        {/* Texto */}
        <div className="ms-text">
          {/* Kicker */}
          <p
            style={{
              fontFamily: "'Hanken Grotesk', sans-serif",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.18em',
              textTransform: 'uppercase' as const,
              color: CHAMPAGNE,
              marginBottom: 20,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 5,
                height: 5,
                borderRadius: '50%',
                background: CHAMPAGNE_LIGHT,
                boxShadow: `0 0 8px ${CHAMPAGNE_LIGHT}`,
              }}
            />
            {kicker}
          </p>

          {/* Titulo */}
          <h2
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontWeight: 400,
              fontSize: 'clamp(28px, 4vw, 48px)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: FG,
              marginBottom: 22,
            }}
          >
            {title}
          </h2>

          {/* Desc */}
          {Array.isArray(desc) ? (
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              {desc.map((item, i) => (
                <li
                  key={i}
                  style={{
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontSize: 'clamp(15px, 1.8vw, 17px)',
                    lineHeight: 1.6,
                    color: MUTED,
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      marginTop: 7,
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: 'rgba(168,85,247,0.7)',
                    }}
                  />
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <p
              style={{
                fontFamily: "'Hanken Grotesk', sans-serif",
                fontSize: 'clamp(15px, 1.8vw, 17px)',
                lineHeight: 1.65,
                color: MUTED,
                margin: 0,
              }}
            >
              {desc}
            </p>
          )}
        </div>

        {/* Ilustracao */}
        <div className="ms-illustration">
          {children}
        </div>
      </section>
    </>
  );
}

// Estilos de layout responsivo e reveal
const MODULE_SECTION_STYLES = `
  .module-section {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.65s ease, transform 0.65s ease;
    scroll-margin-top: 72px;
    grid-template-columns: 1fr;
  }

  /* Itens do grid podem encolher abaixo do conteudo: evita estouro/clip no mobile
     e deixa os wrappers overflow-x:auto (tabelas, calendario) rolarem dentro do card */
  .module-section .ms-text,
  .module-section .ms-illustration { min-width: 0; }

  .module-section.ms-visible {
    opacity: 1;
    transform: translateY(0);
  }

  .module-section.ms-visible .ms-illustration {
    transition-delay: 0.12s;
  }

  /* Mobile: 1 coluna, texto sempre antes da ilustracao */
  .module-section .ms-text { order: 1; }
  .module-section .ms-illustration { order: 2; }

  @media (max-width: 600px) {
    .module-section {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }
  }

  @media (min-width: 860px) {
    .module-section {
      grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
    }
    .module-section.ms-reverse {
      grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    }
    .module-section.ms-reverse .ms-text { order: 2; }
    .module-section.ms-reverse .ms-illustration { order: 1; }
  }
`;
